<script setup lang="ts">
import { getOpenGraph, getTwitter } from '~/helpers/useMetaData'
import type { Locale } from 'lc-services/types'

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: '/contact',
    fr: '/contact',
  },
})
definePageMeta({
  footer: 'compact',
  gtmAdditionalEventData: { page_type: 'contact_form' },
})

const { locale, t } = useI18n<unknown, Locale>({ useScope: 'local' })
const localePath = useLocalePath()
const { isDesktop, isMobile } = useBreakpoint()
const { message: openingTimes } = useOpeningTimes()
const { trackEvent } = useTracking()

const requestSent = ref(false)
const metaTitle = t('title')
const metaDescription = t('description')

useHead({
  title: metaTitle,
  meta: [
    {
      hid: 'description',
      name: 'description',
      content: metaDescription,
    },
    ...getOpenGraph(metaTitle),
    ...getTwitter(metaTitle),
  ],
})
</script>

<template>
  <div class="mx-0 max-w-full px-4 sm:mx-28">
    <BaseRow>
      <BaseCol cols="24" sm="12">
        <h1 class="mb-4 text-6xl">{{ t('title') }}</h1>
        <p>
          {{ t('subtitle') }}
        </p>
        <BaseButton
          class="mt-4 !px-3 !py-2 !text-md !font-semibold"
          color="secondary"
          variant="outline"
          :to="$t('global.faq_link')"
        >
          {{ t('faq') }}
        </BaseButton>
      </BaseCol>
    </BaseRow>

    <BaseRow>
      <BaseCol cols="24" sm="12">
        <h2>{{ t('channels.phone') }}</h2>
      </BaseCol>
      <BaseCol cols="24" sm="12">
        <BasePhoneNumber
          class="text-md !font-bold !text-secondary-400 sm:text-base"
          @click="
            trackEvent({
              event: 'phone_number_click',
              source: 'Burger menu - contact form',
            })
          "
        />
        <p>{{ openingTimes }}</p>
      </BaseCol>
    </BaseRow>

    <BaseRow class="gap-4 sm:gap-0">
      <BaseCol cols="24" sm="12">
        <h2>{{ t('channels.email.title') }}</h2>
        <p :class="{ hidden: isMobile && requestSent }">
          {{ t('channels.email.subtitle') }}
        </p>
      </BaseCol>
      <BaseCol cols="24" sm="12">
        <ContactForm @on-success="requestSent = $event" />
      </BaseCol>
    </BaseRow>

    <BaseRow class="gap-4 sm:gap-0">
      <BaseCol cols="24" sm="12">
        <h2>{{ t('channels.other.title') }}</h2>
        <p>{{ t('channels.other.subtitle') }}</p>
      </BaseCol>
      <BaseCol cols="24" sm="12">
        <NuxtLink
          v-for="name in ['owners', 'realEstate', 'travelPartners', 'hiring']"
          :key="name"
          class="mb-4 flex items-center justify-between rounded border border-gray-200 p-4 text-gray-700 hover:border-gray-700 hover:no-underline"
          :external="name === 'hiring'"
          :target="isDesktop ? '_blank' : 'self'"
          :to="
            name === 'hiring'
              ? `https://careers.flatchr.io/${locale}/company/lecollectionist/`
              : localePath(name).concat(`#${name}Form`)
          "
        >
          <div class="flex grow flex-col gap-1">
            <p class="!text-base font-bold">
              {{ t(`channels.other.${name}.label`) }}
            </p>
            <p class="!text-xs text-gray-500">
              {{ t(`channels.other.${name}.text`) }}
            </p>
          </div>
          <BaseIcon name="arrowRight" :size="1.25" />
        </NuxtLink>
      </BaseCol>
    </BaseRow>
  </div>
</template>

<style scoped>
:deep(.lc-row) {
  @apply py-10;
}
:deep(.lc-row:last-child) {
  @apply mb-20;
}
:deep(.lc-row:not(:last-child)) {
  @apply border-b border-b-gray-200;
}
p {
  @apply m-0 text-md;
}
h2 {
  @apply mb-2 text-2xl;
}
</style>

<i18n lang="yaml">
fr:
  description: Nos équipes sont disponibles pour répondre à toutes vos questions. Vous pouvez également nous contacter directement par téléphone au +33 1 73 03 02 02.
  title: Contactez-nous
  subtitle: Des questions sur nos propriétés, besoin d’aide pour votre réservation, ou envie d’en savoir plus sur nos services ? Notre équipe dédiée est là pour vous aider !
  faq: Consultez notre FAQ
  channels:
    phone: Par téléphone
    email:
      title: Par e-mail
      subtitle: Sélectionnez un sujet et remplissez le formulaire pour nous contacter.
    other:
      title: Autre demande
      subtitle: Pour toute autre demande, consultez l'une des pages dédiées.
      owners:
        label: Proposer votre maison à la location
        text: Pour proposer votre maison à la location, rendez-vous sur cette page
      realEstate:
        label: Acheter une maison
        text: Si vous désirez acquérir une des propriétés, rendez-vous sur cette page
      travelPartners:
        label: Je suis un agent de voyage
        text: Merci de remplir le formulaire sur la page Agences partenaires
      hiring:
        label: Rejoindre nos équipes
        text: Retrouvez toutes nos opportunités sur notre Site carrières
en:
  description: Our team are available to help you for any questions you may have. You can also call us at +33 1 73 03 02 02.
  title: Contact us
  subtitle: Questions about our properties, need help with your booking, or want to know more about our services? Our dedicated team is here to assist you!
  faq: Check our FAQ
  channels:
    phone: Call us
    email:
      title: By email
      subtitle: Select a subject and fill out the form to contact us.
    other:
      title: Other request
      subtitle: For any other request, please refer to one of the dedicated pages.
      owners:
        label: Submit your home for rent
        text: To submit your house for rent, visit this page.
      realEstate:
        label: Buy a house
        text: If you wish to acquire one of the properties, please visit this page.
      travelPartners:
        label: I am a travel agent
        text: Thank you for filling out the form on the Partner Agencies page.
      hiring:
        label: Join our teams
        text: Find all our opportunities on our Careers site.
</i18n>
